export const EXERCISE_FAMILIARITY_OPTIONS = [
  {
    value: 'Novice',
    label: 'Novice',
    description: 'I need simple movements and clear coaching cues.',
  },
  {
    value: 'Basic',
    label: 'Basic',
    description: 'I know basic exercises but prefer straightforward sessions.',
  },
  {
    value: 'Comfortable',
    label: 'Comfortable',
    description: 'I can handle a wide range of common exercises.',
  },
  {
    value: 'Advanced',
    label: 'Advanced',
    description: 'I train confidently and can handle technical variations.',
  },
  {
    value: 'Expert/PT',
    label: 'Expert/PT',
    description: 'I understand programming, form, and advanced movement progressions.',
  },
];

export const FITNESS_ASSESSMENT_DOMAINS = [
  {
    id: 'push',
    label: 'Push',
    prompt: 'How many controlled reps can you complete?',
    unit: 'reps',
    variations: [
      { value: 'easier', label: 'Incline push-up', difficultyLabel: 'Easier' },
      { value: 'standard', label: 'Push-up', difficultyLabel: 'Standard' },
      { value: 'harder', label: 'Decline push-up', difficultyLabel: 'Harder' },
    ],
  },
  {
    id: 'squat',
    label: 'Squat / lunge',
    prompt: 'How many controlled reps can you complete?',
    unit: 'reps',
    variations: [
      { value: 'easier', label: 'Box squat', difficultyLabel: 'Easier' },
      { value: 'standard', label: 'Air squat', difficultyLabel: 'Standard' },
      { value: 'harder', label: 'Reverse lunge', difficultyLabel: 'Harder' },
    ],
  },
  {
    id: 'hinge',
    label: 'Hinge',
    prompt: 'How many controlled reps can you complete?',
    unit: 'reps',
    variations: [
      { value: 'easier', label: 'Glute bridge', difficultyLabel: 'Easier' },
      { value: 'standard', label: 'Hip hinge good morning', difficultyLabel: 'Standard' },
      { value: 'harder', label: 'Single-leg Romanian deadlift', difficultyLabel: 'Harder' },
    ],
  },
  {
    id: 'core',
    label: 'Core',
    prompt: 'How long can you hold steady form?',
    unit: 'seconds',
    variations: [
      { value: 'easier', label: 'Knee plank', difficultyLabel: 'Easier' },
      { value: 'standard', label: 'Forearm plank', difficultyLabel: 'Standard' },
      { value: 'harder', label: 'Long-lever plank', difficultyLabel: 'Harder' },
    ],
  },
  {
    id: 'cardio',
    label: 'Cardio',
    prompt: 'How many minutes can you sustain a challenging but controlled effort?',
    unit: 'minutes',
    variations: [
      { value: 'easier', label: 'Brisk walk', difficultyLabel: 'Easier' },
      { value: 'standard', label: 'Jog or step-ups', difficultyLabel: 'Standard' },
      { value: 'harder', label: 'Fast run or burpees', difficultyLabel: 'Harder' },
    ],
  },
  {
    id: 'mobility',
    label: 'Mobility range',
    prompt: 'Choose the closest pain-free movement range you can control today.',
    unit: 'choice',
    resultInputType: 'none',
    variations: [
      { value: 'very_limited', label: 'Very limited or painful', difficultyLabel: 'Very limited' },
      { value: 'limited', label: 'Limited, cautious range', difficultyLabel: 'Limited' },
      { value: 'standard', label: 'Normal daily movement', difficultyLabel: 'Normal' },
      { value: 'deep_controlled', label: 'Deep range with control', difficultyLabel: 'Deep' },
      { value: 'deep_unrestricted', label: 'Deep range, no discomfort', difficultyLabel: 'Full' },
    ],
  },
];

export const FITNESS_ASSESSMENT_DIFFICULTY_STYLES = {
  easier: {
    label: 'Easier',
    className: 'border-emerald-400/40 bg-emerald-400/10 text-emerald-100',
  },
  standard: {
    label: 'Standard',
    className: 'border-cyan-400/40 bg-cyan-400/10 text-cyan-100',
  },
  harder: {
    label: 'Harder',
    className: 'border-amber-400/40 bg-amber-400/10 text-amber-100',
  },
  very_limited: {
    label: 'Very limited',
    className: 'border-red-400/40 bg-red-400/10 text-red-100',
  },
  limited: {
    label: 'Limited',
    className: 'border-amber-400/40 bg-amber-400/10 text-amber-100',
  },
  deep_controlled: {
    label: 'Deep',
    className: 'border-blue-400/40 bg-blue-400/10 text-blue-100',
  },
  deep_unrestricted: {
    label: 'Full',
    className: 'border-emerald-400/40 bg-emerald-400/10 text-emerald-100',
  },
};

const VARIATION_LEVELS = {
  easier: 0,
  standard: 1,
  harder: 2,
};

const RESULT_THRESHOLDS = {
  push: [1, 5, 12, 25],
  squat: [3, 8, 18, 35],
  hinge: [3, 8, 15, 30],
  core: [15, 30, 60, 120],
  cardio: [3, 8, 15, 25],
  mobility: [1, 2, 3, 4],
};

const MOBILITY_VARIATION_SCORES = {
  very_limited: 1,
  limited: 2,
  standard: 3,
  deep_controlled: 4,
  deep_unrestricted: 5,
};

const LEGACY_MOBILITY_VARIATION_SCORES = {
  easier: 2,
  standard: 3,
  harder: 4,
};

const normalizeVariationForDomain = (domainId, value) => {
  const normalized = typeof value === 'string' ? value.trim().toLowerCase() : '';
  if (domainId === 'mobility') {
    if (Object.prototype.hasOwnProperty.call(MOBILITY_VARIATION_SCORES, normalized)) {
      return normalized;
    }
    if (Object.prototype.hasOwnProperty.call(LEGACY_MOBILITY_VARIATION_SCORES, normalized)) {
      return normalized;
    }
    return 'standard';
  }
  return Object.prototype.hasOwnProperty.call(VARIATION_LEVELS, normalized)
    ? normalized
    : 'standard';
};

export const createInitialAssessmentAnswers = () =>
  FITNESS_ASSESSMENT_DOMAINS.reduce((acc, domain) => {
    acc[domain.id] = { variation: 'standard', result: '' };
    return acc;
  }, {});

export const normalizeAssessmentAnswers = (answers = {}) =>
  FITNESS_ASSESSMENT_DOMAINS.reduce((acc, domain) => {
    const answer = answers[domain.id] || {};
    const variation = normalizeVariationForDomain(domain.id, answer.variation);
    const numeric = Number(answer.result);
    acc[domain.id] = {
      variation,
      result: Number.isFinite(numeric) && numeric >= 0 ? numeric : 0,
    };
    return acc;
  }, {});

export const scoreAssessmentDomain = (domainId, answer = {}) => {
  const normalized = normalizeAssessmentAnswers({ [domainId]: answer })[domainId];
  if (domainId === 'mobility') {
    if (Object.prototype.hasOwnProperty.call(MOBILITY_VARIATION_SCORES, normalized.variation)) {
      return MOBILITY_VARIATION_SCORES[normalized.variation];
    }
    if (normalized.result > 0) {
      return Math.max(1, Math.min(5, Math.round(normalized.result)));
    }
    return LEGACY_MOBILITY_VARIATION_SCORES[normalized.variation] || 3;
  }

  const thresholds = RESULT_THRESHOLDS[domainId] || [];
  const baseScore = thresholds.reduce((score, threshold) => (
    normalized.result >= threshold ? score + 1 : score
  ), 0);
  const variationBonus = VARIATION_LEVELS[normalized.variation] - VARIATION_LEVELS.standard;
  return Math.max(0, Math.min(5, baseScore + variationBonus));
};

export const scoreFitnessAssessment = (answers = {}) => {
  const normalizedAnswers = normalizeAssessmentAnswers(answers);
  const domainScores = {};
  let totalScore = 0;

  for (const domain of FITNESS_ASSESSMENT_DOMAINS) {
    const score = scoreAssessmentDomain(domain.id, normalizedAnswers[domain.id]);
    domainScores[domain.id] = score;
    totalScore += score;
  }

  return {
    answers: normalizedAnswers,
    domainScores,
    totalScore,
    assignedLevel: Math.max(1, Math.min(30, totalScore)),
  };
};
